import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECT_OPTIONS = ["Hindi", "English", "Maths", "Science", "Coding", "Not sure yet"];

const inputCls = "bg-cream rounded-lg border-border focus-visible:ring-saffron";

export const EnquiryForm = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setDone(true);
            toast.success("Enquiry sent — we will be in touch within 24 hours.");
        } catch {
            toast.error("Something went wrong. Please try again or message us on WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-3xl border border-border bg-sand p-10 text-center card-shadow" data-testid="enquiry-success-message">
                <p className="font-serif text-2xl font-semibold text-teal">Dhanyavaad — thank you!</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Your enquiry has been received. A member of our team will reply within 24 hours.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
            <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="enq-name">Your name</Label>
                    <Input id="enq-name" data-testid="enquiry-name-input" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Priya Kapoor" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="enq-email">Email</Label>
                    <Input id="enq-email" type="email" data-testid="enquiry-email-input" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.co.uk" />
                </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="enq-phone">Phone / WhatsApp (optional)</Label>
                    <Input id="enq-phone" data-testid="enquiry-phone-input" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+1 555 123 4567" />
                </div>
                <div className="space-y-2">
                    <Label>Subject of interest</Label>
                    <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                        <SelectTrigger className={inputCls} data-testid="enquiry-subject-select">
                            <SelectValue placeholder="Choose a subject" />
                        </SelectTrigger>
                        <SelectContent>
                            {SUBJECT_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`enquiry-subject-option-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="enq-message">How can we help?</Label>
                <Textarea id="enq-message" data-testid="enquiry-message-input" required rows={4} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls}
                    placeholder="Tell us about your child, their year group and what you'd like them to achieve." />
            </div>
            <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="w-full rounded-full bg-saffron px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-saffron-dark warm-shadow disabled:opacity-60 sm:w-auto"
            >
                {sending ? "Sending…" : "Send Enquiry"}
            </button>
        </form>
    );
};
